import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import Button from "../../../components/button/Button";
import Select from "react-select";
import BaseModal from "../../../components/modals/baseModal/BaseModal";
import CompanySelectionModal from "../../../components/modals/companySelectionModal/CompanySelectionModal";
import BillingCompanySelectionModal from "../../../components/modals/billingCompanySelectionModal/BillingCompanySelectionModal";
import styles from "./CreateInvoiceSchemaScreen.module.scss";
import billingInvoicesService from "../../../apis/services/admin/billingInvoicesService";
import taxPayersService from "../../../apis/services/admin/taxPayersService";
import invoicePrefixesService from "../../../apis/services/admin/invoicePrefixesService";
import objectCodesService from "../../../apis/services/admin/objectCodesService";

const NUMBERING_STRATEGY_OPTIONS = [
  { value: 1, label: "Prefiks-Godina-Mesec-Redni broj (INV-2025-01-001)" },
  { value: 2, label: "Prefiks-Godina-Redni broj (INV-2025-001)" },
  { value: 3, label: "Prefiks-Godina-ID kompanije-Redni broj (INV-2025-55-001)" },
  { value: 4, label: "Šifra objekta-Redni broj/Godina (2111002884/25)" },
];

const BILLING_PERIOD_OPTIONS = [
  { value: "monthly", label: "Mesečno" },
  { value: "quarterly", label: "Kvartalno" },
  { value: "semi-annual", label: "Polugodišnje" },
  { value: "annual", label: "Godišnje" },
];

const CreateInvoiceSchemaScreen = () => {
  const history = useHistory();
  const location = useLocation();
  const isMountedRef = useRef(true);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [billingCompanyId, setBillingCompanyId] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedBillingCompany, setSelectedBillingCompany] = useState(null);
  const [defaultBillingPeriodType, setDefaultBillingPeriodType] = useState({ value: "monthly", label: "Mesečno" });
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Invoice Prefix
  const [prefixes, setPrefixes] = useState([]);
  const [selectedPrefix, setSelectedPrefix] = useState(null);
  const [invoicePrefixId, setInvoicePrefixId] = useState(null);
  
  // Numbering Strategy
  const [numberingStrategy, setNumberingStrategy] = useState(null);
  const [objectCodes, setObjectCodes] = useState([]);
  const [selectedObjectCode, setSelectedObjectCode] = useState(null);
  const [objectCodeId, setObjectCodeId] = useState(null);
  
  // Effective Dates
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveTo, setEffectiveTo] = useState("");
  
  // Modal states
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [billingCompanyModalOpen, setBillingCompanyModalOpen] = useState(false);
  const [newBillingCompanyModalOpen, setNewBillingCompanyModalOpen] = useState(false);
  
  // Billing Company Creation Form
  const [newBillingCompanyName, setNewBillingCompanyName] = useState("");
  const [newBillingCompanyAddress, setNewBillingCompanyAddress] = useState("");
  const [newBillingCompanyCity, setNewBillingCompanyCity] = useState("");
  const [newBillingCompanyZipCode, setNewBillingCompanyZipCode] = useState("");
  const [newBillingCompanyTaxId, setNewBillingCompanyTaxId] = useState("");
  const [newBillingCompanyEmail, setNewBillingCompanyEmail] = useState("");
  const [newBillingCompanyPhone, setNewBillingCompanyPhone] = useState("");
  const [newBillingCompanyContactFirstName, setNewBillingCompanyContactFirstName] = useState("");
  const [newBillingCompanyContactLastName, setNewBillingCompanyContactLastName] = useState("");
  const [creatingBillingCompany, setCreatingBillingCompany] = useState(false);

  // Schema Items
  const [schemaItems, setSchemaItems] = useState([
    { name: "", description: "", unitPrice: "", quantity: "", discountQuantity: "", discountPercent: "", isActive: true, order: 0 },
  ]);

  // Handle company selection from modal
  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setCompanyId(company.id);
  };

  // Handle billing company selection from modal
  const handleBillingCompanySelect = (billingCompany) => {
    setSelectedBillingCompany(billingCompany);
    setBillingCompanyId(billingCompany.id);
  };

  // Handle new billing company button - prefill with company data if company is selected
  const handleNewBillingCompanyClick = () => {
    if (selectedCompany) {
      // Prefill billing company form with company data
      setNewBillingCompanyName(selectedCompany.name || "");
      setNewBillingCompanyTaxId(selectedCompany.PIB || "");
      if (selectedCompany.address) {
        setNewBillingCompanyAddress(selectedCompany.address.name || "");
      }
      // Try to get user info from company if available
      if (selectedCompany.users && selectedCompany.users.length > 0) {
        const firstUser = selectedCompany.users[0];
        setNewBillingCompanyContactFirstName(firstUser.firstName || "");
        setNewBillingCompanyContactLastName(firstUser.lastName || "");
      }
    }
    setNewBillingCompanyModalOpen(true);
  };

  // Prefill company from URL params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const prefilledCompanyId = searchParams.get('companyId');
    if (prefilledCompanyId && !selectedCompany) {
      taxPayersService.fetchTaxpayers(1, 200, "")
        .then(response => {
          const allCompanies = response.data?.data || [];
          const company = allCompanies.find(c => 
            c.id === parseInt(prefilledCompanyId) || 
            c.id.toString() === prefilledCompanyId.toString()
          );
          if (company && isMountedRef.current) {
            handleCompanySelect(company);
          }
        })
        .catch(err => console.error("Error fetching company for prefilling:", err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load invoice prefixes
  useEffect(() => {
    const loadPrefixes = async () => {
      try {
        const response = await invoicePrefixesService.getInvoicePrefixes(1, 100, true, "", "id", "ASC");
        if (isMountedRef.current) {
          const prefixesData = response.data?.data || [];
          setPrefixes(prefixesData);
        }
      } catch (error) {
        console.error("Error loading invoice prefixes:", error);
      }
    };
    loadPrefixes();
  }, []);

  // Load object codes when numbering strategy is 4
  useEffect(() => {
    const loadObjectCodes = async () => {
      if (numberingStrategy === 4) {
        try {
          const response = await objectCodesService.getObjectCodes(1, 100, true, "", "id", "ASC");
          if (isMountedRef.current) {
            const objectCodesData = response.data?.data || [];
            setObjectCodes(objectCodesData);
          }
        } catch (error) {
          console.error("Error loading object codes:", error);
        }
      } else {
        setObjectCodes([]);
        setSelectedObjectCode(null);
        setObjectCodeId(null);
      }
    };
    loadObjectCodes();
  }, [numberingStrategy]);

  const handleAddItem = () => {
    setSchemaItems([
      ...schemaItems,
      { name: "", description: "", unitPrice: "", quantity: "", discountQuantity: "", discountPercent: "", isActive: true, order: schemaItems.length },
    ]);
  };

  const handleRemoveItem = (index) => {
    if (schemaItems.length > 1) {
      const newItems = schemaItems.filter((_, i) => i !== index);
      setSchemaItems(newItems.map((item, i) => ({ ...item, order: i })));
    } else {
      toast.error("Mora postojati barem jedna stavka");
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...schemaItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setSchemaItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Molimo unesite naziv šeme");
      return;
    }

    if (!billingCompanyId) {
      toast.error("Molimo izaberite fakturisku kompaniju");
      return;
    }

    if (!companyId) {
      toast.error("Molimo izaberite kompaniju");
      return;
    }

    // Validate items
    for (let i = 0; i < schemaItems.length; i++) {
      const item = schemaItems[i];
      if (!item.name.trim()) {
        toast.error(`Stavka ${i + 1}: Molimo unesite naziv`);
        return;
      }
      if (!item.unitPrice || parseFloat(item.unitPrice) <= 0) {
        toast.error(`Stavka ${i + 1}: Molimo unesite validnu cenu`);
        return;
      }
      // Validate discount fields - only one should be set
      if (item.discountQuantity && item.discountPercent) {
        toast.error(`Stavka ${i + 1}: Možete koristiti samo popust količinu ili popust procenat, ne oba`);
        return;
      }
      if (item.discountPercent && item.discountPercent.trim() !== "" && (parseFloat(item.discountPercent) < 0 || parseFloat(item.discountPercent) > 100)) {
        toast.error(`Stavka ${i + 1}: Popust procenat mora biti između 0 i 100`);
        return;
      }
    }

    // Validate numbering strategy
    if (numberingStrategy === 4 && !objectCodeId) {
      toast.error("Molimo izaberite šifru objekta za strategiju numerisanja 4");
      return;
    }

    setSubmitting(true);

    try {
      const schemaData = {
        name: name.trim(),
        notes: description.trim() || null,
        billingCompanyId: parseInt(billingCompanyId),
        companyId: parseInt(companyId),
        invoicePrefixId: invoicePrefixId || null,
        numberingStrategy: numberingStrategy || null,
        objectCodeId: objectCodeId || null,
        effectiveFrom: effectiveFrom ? new Date(effectiveFrom).toISOString() : null,
        effectiveTo: effectiveTo ? new Date(effectiveTo).toISOString() : null,
        defaultBillingPeriodType: typeof defaultBillingPeriodType === "object" ? defaultBillingPeriodType.value : defaultBillingPeriodType,
        isActive,
        items: schemaItems.map((item, index) => {
          const itemData = {
            name: item.name.trim(),
            description: item.description.trim() || null,
            unitPrice: parseFloat(item.unitPrice),
            order: index,
            isActive: item.isActive !== undefined ? item.isActive : true,
          };

          // Add quantity only if provided
          if (item.quantity && item.quantity.trim() !== "") {
            itemData.quantity = parseInt(item.quantity);
          }

          // Add discountQuantity only if provided
          if (item.discountQuantity && item.discountQuantity.trim() !== "") {
            itemData.discountQuantity = parseInt(item.discountQuantity);
          }

          // Add discountPercent only if provided and valid
          if (item.discountPercent && item.discountPercent.trim() !== "") {
            const discountPercentValue = parseFloat(item.discountPercent);
            if (!isNaN(discountPercentValue) && discountPercentValue >= 0 && discountPercentValue <= 100) {
              itemData.discountPercent = discountPercentValue;
            }
          }

          return itemData;
        }),
      };

      await billingInvoicesService.createInvoiceSchema(schemaData);
      toast.success("Šema fakture uspešno kreirana");
      history.push("/billing-invoices");
    } catch (error) {
      toast.error("Greška pri kreiranju šeme fakture");
      console.error("Error creating invoice schema:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateBillingCompany = async () => {
    if (!newBillingCompanyName.trim()) {
      toast.error("Molimo unesite naziv fakturiske kompanije");
      return;
    }

    setCreatingBillingCompany(true);

    try {
      const companyData = {
        name: newBillingCompanyName.trim(),
        address: newBillingCompanyAddress.trim() || undefined,
        city: newBillingCompanyCity.trim() || undefined,
        zipCode: newBillingCompanyZipCode.trim() || undefined,
        taxId: newBillingCompanyTaxId.trim() || undefined,
        email: newBillingCompanyEmail.trim() || undefined,
        contactEmail: newBillingCompanyEmail.trim() || undefined,
        phone: newBillingCompanyPhone.trim() || undefined,
        contactPhone: newBillingCompanyPhone.trim() || undefined,
        contactFirstName: newBillingCompanyContactFirstName.trim() || undefined,
        contactLastName: newBillingCompanyContactLastName.trim() || undefined,
      };

      // Remove undefined values
      Object.keys(companyData).forEach(key => {
        if (companyData[key] === undefined) {
          delete companyData[key];
        }
      });

      const response = await billingInvoicesService.createBillingCompany(companyData);
      toast.success("Fakturiska kompanija uspešno kreirana");
      
      // Select the newly created billing company
      const newBillingCompany = response.data || response;
      handleBillingCompanySelect(newBillingCompany);
      
      // Reset form
      setNewBillingCompanyName("");
      setNewBillingCompanyAddress("");
      setNewBillingCompanyCity("");
      setNewBillingCompanyZipCode("");
      setNewBillingCompanyTaxId("");
      setNewBillingCompanyEmail("");
      setNewBillingCompanyPhone("");
      setNewBillingCompanyContactFirstName("");
      setNewBillingCompanyContactLastName("");
      setNewBillingCompanyModalOpen(false);
    } catch (error) {
      toast.error("Greška pri kreiranju fakturiske kompanije");
      console.error("Error creating billing company:", error);
    } finally {
      setCreatingBillingCompany(false);
    }
  };

  return (
    <DashboardLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Nova šema fakture</h1>
          <button className={styles.backButton} onClick={() => history.goBack()}>
            ← Nazad
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Osnovne informacije</h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Naziv *</label>
                <input
                  type="text"
                  className={styles.textInput}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Naziv šeme fakture"
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Fakturiska kompanija *</label>
                <div className={styles.selectWithButton}>
                  <button
                    type="button"
                    className={styles.selectButton}
                    onClick={() => setBillingCompanyModalOpen(true)}
                  >
                    {selectedBillingCompany ? selectedBillingCompany.name : "Izaberite fakturisku kompaniju"}
                  </button>
                  <button
                    type="button"
                    className={styles.addButton}
                    onClick={handleNewBillingCompanyClick}
                    title="Dodaj novu fakturisku kompaniju"
                  >
                    +
                  </button>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Kompanija *</label>
                <button
                  type="button"
                  className={styles.selectButton}
                  onClick={() => setCompanyModalOpen(true)}
                >
                  {selectedCompany ? selectedCompany.name : "Izaberite kompaniju"}
                </button>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Opis</label>
              <textarea
                className={styles.textarea}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Opis šeme fakture"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Prefiks fakture</label>
              <Select
                value={selectedPrefix}
                onChange={(option) => {
                  setSelectedPrefix(option);
                  setInvoicePrefixId(option ? option.value : null);
                }}
                options={[
                  { value: null, label: "Koristi podrazumevani prefiks" },
                  ...prefixes.map((prefix) => ({
                    value: prefix.id,
                    label: `${prefix.prefix}${prefix.isDefault ? " (Podrazumevani)" : ""}`,
                  })),
                ]}
                placeholder="Izaberite prefiks..."
                isClearable
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: "40px",
                  }),
                }}
              />
              <p className={styles.helperText}>
                Ako ne izaberete prefiks, koristiće se podrazumevani prefiks
              </p>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Strategija numerisanja faktura</label>
              <Select
                value={numberingStrategy ? NUMBERING_STRATEGY_OPTIONS.find(opt => opt.value === numberingStrategy) : null}
                onChange={(option) => {
                  setNumberingStrategy(option ? option.value : null);
                  if (option?.value !== 4) {
                    setSelectedObjectCode(null);
                    setObjectCodeId(null);
                  }
                }}
                options={NUMBERING_STRATEGY_OPTIONS}
                placeholder="Izaberite strategiju numerisanja..."
                isClearable
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: "40px",
                  }),
                }}
              />
              <p className={styles.helperText}>
                Strategija određuje format broja fakture. Ako ne izaberete, koristiće se podrazumevna strategija (1).
              </p>
            </div>

            {numberingStrategy === 4 && (
              <div className={styles.formGroup}>
                <label className={styles.label}>Šifra objekta *</label>
                <Select
                  value={selectedObjectCode}
                  onChange={(option) => {
                    setSelectedObjectCode(option);
                    setObjectCodeId(option ? option.value : null);
                  }}
                  options={objectCodes.map((code) => ({
                    value: code.id,
                    label: `${code.code || code.objectCode || code.name}${code.name ? ` - ${code.name}` : ""}`,
                  }))}
                  placeholder="Izaberite šifru objekta..."
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "40px",
                    }),
                  }}
                />
                <p className={styles.helperText}>
                  Šifra objekta je obavezna za strategiju numerisanja 4
                </p>
              </div>
            )}

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Važi od</label>
                <input
                  type="datetime-local"
                  className={styles.textInput}
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                />
                <p className={styles.helperText}>
                  Datum kada šema postaje aktivna (opciono)
                </p>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Važi do</label>
                <input
                  type="datetime-local"
                  className={styles.textInput}
                  value={effectiveTo}
                  onChange={(e) => setEffectiveTo(e.target.value)}
                  min={effectiveFrom || undefined}
                />
                <p className={styles.helperText}>
                  Datum kada šema prestaje da važi (opciono)
                </p>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Podrazumevani tip perioda *</label>
                <Select
                  options={BILLING_PERIOD_OPTIONS}
                  value={defaultBillingPeriodType}
                  onChange={setDefaultBillingPeriodType}
                  placeholder="Izaberite tip perioda"
                  styles={{
                    container: (provided) => ({
                      ...provided,
                      width: "100%",
                    }),
                    control: (provided) => ({
                      ...provided,
                      minHeight: "50px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      "&:hover": {
                        borderColor: "#007bff",
                      },
                    }),
                  }}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className={styles.checkbox}
                />
                Aktivna šema
              </label>
              <p className={styles.helperText}>
                Samo aktivne šeme se koriste za automatsko generisanje faktura
              </p>
            </div>
          </div>

          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Stavke šeme</h2>
              <Button name="+ Dodaj stavku" onClick={handleAddItem} type="button" />
            </div>

            {schemaItems.map((item, index) => (
              <div key={index} className={styles.itemCard}>
                <div className={styles.itemHeader}>
                  <h3 className={styles.itemTitle}>Stavka {index + 1}</h3>
                  {schemaItems.length > 1 && (
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => handleRemoveItem(index)}
                    >
                      Obriši
                    </button>
                  )}
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Naziv stavke *</label>
                    <input
                      type="text"
                      className={styles.textInput}
                      value={item.name}
                      onChange={(e) => handleItemChange(index, "name", e.target.value)}
                      placeholder="Npr. Fiskalni uređaj, Vending mašina"
                    />
                    <p className={styles.helperText}>
                      Naziv stavke koji će se koristiti za automatsko prebrojavanje
                    </p>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Cena po jedinici *</label>
                    <input
                      type="number"
                      step="0.01"
                      className={styles.textInput}
                      value={item.unitPrice}
                      onChange={(e) => handleItemChange(index, "unitPrice", e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Količina (fiksna)</label>
                    <input
                      type="number"
                      className={styles.textInput}
                      value={item.quantity || ""}
                      onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                      placeholder="Ostavite prazno za automatsko prebrojavanje"
                    />
                    <p className={styles.helperText}>
                      Fiksna količina (npr. 1 za baznu pretplatu). Ostavite prazno za automatsko prebrojavanje (fiskalni uređaji, vending mašine).
                    </p>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Opis</label>
                    <textarea
                      className={styles.textarea}
                      value={item.description || ""}
                      onChange={(e) => handleItemChange(index, "description", e.target.value)}
                      rows={2}
                      placeholder="Opis stavke (opciono)"
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Popust količina (besplatne stavke)</label>
                    <input
                      type="number"
                      className={styles.textInput}
                      value={item.discountQuantity || ""}
                      onChange={(e) => handleItemChange(index, "discountQuantity", e.target.value)}
                      placeholder="0"
                    />
                    <p className={styles.helperText}>
                      Broj besplatnih stavki (npr. ako ima 8 uređaja, a popust je 3, naplaćuje se za 5)
                    </p>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Popust procenat (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      className={styles.textInput}
                      value={item.discountPercent || ""}
                      onChange={(e) => handleItemChange(index, "discountPercent", e.target.value)}
                      placeholder="0"
                    />
                    <p className={styles.helperText}>
                      Popust u procentima (0-100). Koristite samo popust količinu ILI popust procenat.
                    </p>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={item.isActive !== undefined ? item.isActive : true}
                      onChange={(e) => handleItemChange(index, "isActive", e.target.checked)}
                      className={styles.checkbox}
                    />
                    Aktivna stavka
                  </label>
                  <p className={styles.helperText}>
                    Neaktivne stavke se ne koriste pri generisanju faktura
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.formActions}>
            <Button
              name="Otkaži"
              onClick={(e) => {
                e.preventDefault();
                history.goBack();
              }}
              type="button"
            />
            <Button
              name={submitting ? "Kreiranje..." : "Kreiraj šemu"}
              onClick={handleSubmit}
              disabled={submitting}
              showLoader={submitting}
              type="submit"
            />
          </div>
        </form>
      </div>

      {/* Create Billing Company Modal */}
      <BaseModal modalOpen={newBillingCompanyModalOpen} onClose={() => {
        setNewBillingCompanyModalOpen(false);
        setNewBillingCompanyName("");
        setNewBillingCompanyAddress("");
        setNewBillingCompanyCity("");
        setNewBillingCompanyZipCode("");
        setNewBillingCompanyTaxId("");
        setNewBillingCompanyEmail("");
        setNewBillingCompanyPhone("");
        setNewBillingCompanyContactFirstName("");
        setNewBillingCompanyContactLastName("");
      }}>
        <div className={styles.modalContent}>
          <h2 className={styles.modalTitle}>Nova fakturiska kompanija</h2>
          <div className={styles.modalForm}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Naziv *</label>
              <input
                type="text"
                className={styles.textInput}
                value={newBillingCompanyName}
                onChange={(e) => setNewBillingCompanyName(e.target.value)}
                placeholder="Naziv fakturiske kompanije"
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Adresa</label>
                <input
                  type="text"
                  className={styles.textInput}
                  value={newBillingCompanyAddress}
                  onChange={(e) => setNewBillingCompanyAddress(e.target.value)}
                  placeholder="Adresa"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Grad</label>
                <input
                  type="text"
                  className={styles.textInput}
                  value={newBillingCompanyCity}
                  onChange={(e) => setNewBillingCompanyCity(e.target.value)}
                  placeholder="Grad"
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Poštanski broj</label>
                <input
                  type="text"
                  className={styles.textInput}
                  value={newBillingCompanyZipCode}
                  onChange={(e) => setNewBillingCompanyZipCode(e.target.value)}
                  placeholder="Poštanski broj"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>PIB</label>
                <input
                  type="text"
                  className={styles.textInput}
                  value={newBillingCompanyTaxId}
                  onChange={(e) => setNewBillingCompanyTaxId(e.target.value)}
                  placeholder="PIB"
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Email</label>
                <input
                  type="email"
                  className={styles.textInput}
                  value={newBillingCompanyEmail}
                  onChange={(e) => setNewBillingCompanyEmail(e.target.value)}
                  placeholder="Email"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Telefon</label>
                <input
                  type="text"
                  className={styles.textInput}
                  value={newBillingCompanyPhone}
                  onChange={(e) => setNewBillingCompanyPhone(e.target.value)}
                  placeholder="Telefon"
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Ime kontakt osobe</label>
                <input
                  type="text"
                  className={styles.textInput}
                  value={newBillingCompanyContactFirstName}
                  onChange={(e) => setNewBillingCompanyContactFirstName(e.target.value)}
                  placeholder="Ime"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Prezime kontakt osobe</label>
                <input
                  type="text"
                  className={styles.textInput}
                  value={newBillingCompanyContactLastName}
                  onChange={(e) => setNewBillingCompanyContactLastName(e.target.value)}
                  placeholder="Prezime"
                />
              </div>
            </div>

            <div className={styles.modalActions}>
              <Button
                name={creatingBillingCompany ? "Kreiranje..." : "Kreiraj"}
                onClick={handleCreateBillingCompany}
                disabled={creatingBillingCompany}
                showLoader={creatingBillingCompany}
              />
              <Button
                name="Otkaži"
                onClick={() => {
                  setNewBillingCompanyModalOpen(false);
                  setNewBillingCompanyName("");
                  setNewBillingCompanyAddress("");
                  setNewBillingCompanyCity("");
                  setNewBillingCompanyZipCode("");
                  setNewBillingCompanyTaxId("");
                  setNewBillingCompanyEmail("");
                  setNewBillingCompanyPhone("");
                  setNewBillingCompanyContactFirstName("");
                  setNewBillingCompanyContactLastName("");
                }}
              />
            </div>
          </div>
        </div>
      </BaseModal>

      <CompanySelectionModal
        modalOpen={companyModalOpen}
        onClose={() => setCompanyModalOpen(false)}
        onChoose={handleCompanySelect}
        selectedCompanyId={companyId}
      />

      <BillingCompanySelectionModal
        modalOpen={billingCompanyModalOpen}
        onClose={() => setBillingCompanyModalOpen(false)}
        onChoose={handleBillingCompanySelect}
        selectedBillingCompanyId={billingCompanyId}
      />
    </DashboardLayout>
  );
};

export default CreateInvoiceSchemaScreen;
